import React from 'react';
import { Box, Container, Card, Alert, Skeleton, useTheme, useMediaQuery } from '@mui/material';
import ProfileIdentityRail from './ProfileIdentityRail';

/**
 * The presentational frame every profile page shares: app bar slot, container,
 * header slot, error and loading states, and the card holding the identity rail
 * beside the active tab panel.
 *
 * It owns layout only. Fetching, the shape of the response, permission gating,
 * and which tabs exist all stay with the page, because those are precisely
 * where the two callers differ: UserDetail reads GET /admin/users/:id behind
 * can_view_user_details and renders a 404 when it is missing, while Account
 * reads GET /user for whoever is logged in. Merging those two data paths is
 * what this extraction exists to avoid.
 *
 * Chrome belongs to the caller for the same reason, which is why `appBar` is a
 * slot rather than a fixed import. This shell once hard-rendered StaffAppBar,
 * so the self-service Account page came out branded "FiStone Admin Dashboard",
 * wearing an ADMIN chip and offering a System Admin link — staff chrome on a
 * user's own account page, arriving from a component that is supposed to decide
 * nothing but layout. UserDetail passes StaffAppBar; Account passes UserAppBar,
 * the same bar the Dashboard it is reached from uses.
 *
 * `header` is whatever the page wants above the card, and the two callers
 * deliberately pass different things. UserDetail is reachable only from the
 * admin dashboard, so it passes a breadcrumb back to it; Account is reachable
 * from every app bar in the product, so a crumb there named a dashboard the
 * reader had probably not come from, and it passes a plain page title instead —
 * the same one the dashboards and System Administration open with. The slot was
 * called `breadcrumbs` while a breadcrumb was the only thing in it.
 *
 * `profile` is the canonical shape documented on ProfileIdentityRail — each
 * page adapts its own DTO before passing it in.
 *
 * `children` is the body of the active tab. It is only rendered once `profile`
 * is non-null, so a caller may write `{activeTab === 0 && <OverviewTab
 * profile={profile} />}` without guarding against the loading state: React
 * elements are lazy, so creating one from a null profile never calls the
 * component.
 */
function ProfileShell({
  profile,
  loading,
  error,
  appBar,
  header,
  tabs,
  idPrefix,
  tabsAriaLabel,
  activeTab,
  onTabChange,
  children,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ flexGrow: 1 }}>
      {appBar}

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {header}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading && !profile && (
          <Card sx={{ p: 3 }}>
            <Skeleton variant="rectangular" height={220} />
          </Card>
        )}

        {profile && (
          <Card>
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
              <ProfileIdentityRail
                profile={profile}
                tabs={tabs}
                idPrefix={idPrefix}
                ariaLabel={tabsAriaLabel}
                activeTab={activeTab}
                onTabChange={onTabChange}
                isMobile={isMobile}
              />
              <Box
                role="tabpanel"
                id={`${idPrefix}-tabpanel-${activeTab}`}
                aria-labelledby={`${idPrefix}-tab-${activeTab}`}
                sx={{ flex: 1, p: 3 }}
              >
                {children}
              </Box>
            </Box>
          </Card>
        )}
      </Container>
    </Box>
  );
}

export default ProfileShell;
